import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Trophy } from 'lucide-react';
import { Gallery } from './Gallery';
import { fadeInUp } from '../../lib/animations';
import { staggerContainer } from '../../lib/animations';
import { useLanguage } from '../../context/LanguageContext';

export const Pricing = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const courtPricing = [
    { time: t.pricing.weekdayBefore5, price: t.pricing.price58, popular: true },
    { time: t.pricing.weekdayAfter5, price: t.pricing.price68, popular: false },
    { time: t.pricing.weekend, price: t.pricing.price68, popular: false }
  ];

  const trainingPricing = [
    { type: t.pricing.privateBefore5, price: t.pricing.price120, popular: false },
    { type: t.pricing.privateAfter5, price: t.pricing.price140, popular: false }
  ];

  return (
    <section id="pricing" ref={ref} className="py-32 dark-section" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <p className="overline text-slate-400 mb-4">PREISE</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            {t.pricing.title}
          </h2>
          <p className="text-xl text-slate-300">
            {t.pricing.subtitle}
          </p>
        </motion.div>

        {/* Court Pricing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-white text-center mb-8">{t.pricing.courtRates}</h3>
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {courtPricing.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className={`relative bg-white/5 backdrop-blur-sm border rounded-3xl p-8 transition-all duration-300 ${
                  item.popular 
                    ? 'border-sky-400 shadow-2xl shadow-sky-400/20 scale-105' 
                    : 'border-white/10 hover:border-white/20'
                }`}
              >
                {item.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-sky-400 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                      Beste Zeit
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h4 className="text-lg font-semibold text-white mb-4">{item.time}</h4>
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-5xl font-bold text-white">{item.price.replace(' CHF', '')}</span>
                    <span className="text-xl text-slate-400 ml-2">CHF</span>
                  </div>
                  <p className="text-slate-400 text-sm">{t.pricing.perHour}</p>
                </div>

                <a
                  href="https://bookpadelarena.ch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full text-center px-6 py-3 rounded-xl font-semibold transition-all ${
                    item.popular
                      ? 'bg-sky-400 text-white hover:bg-sky-300'
                      : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
                  }`}
                >
                  {t.nav.bookNow}
                </a>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Training Pricing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
        >
          <h3 className="text-2xl font-bold text-white text-center mb-4">{t.pricing.trainingTitle}</h3>
          <p className="text-center text-slate-400 mb-8">{t.pricing.trainingSubtitle}</p>

          {/* Training Price Table */}
          <div className="max-w-6xl mx-auto bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 overflow-x-auto">
            <table className="w-full text-white">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-4 px-4 text-slate-300 font-semibold"></th>
                  <th className="text-center py-4 px-4 text-slate-300 font-semibold">{t.pricing.before5}</th>
                  <th className="text-center py-4 px-4 text-slate-300 font-semibold">{t.pricing.after5Weekend}</th>
                </tr>
              </thead>
              <tbody>
                {/* 1 Hour */}
                <tr className="border-b border-white/10">
                  <td colSpan="3" className="py-4 px-4 font-bold text-lg text-sky-400">{t.pricing.hour1} {t.pricing.trainingIncludes}</td>
                </tr>
                <tr className="hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4">{t.pricing.players1}</td>
                  <td className="py-3 px-4 text-center font-bold">120 CHF</td>
                  <td className="py-3 px-4 text-center font-bold">140 CHF</td>
                </tr>
                <tr className="hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4">{t.pricing.players2}</td>
                  <td className="py-3 px-4 text-center font-bold">70 CHF</td>
                  <td className="py-3 px-4 text-center font-bold">80 CHF</td>
                </tr>
                <tr className="hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4">{t.pricing.players3}</td>
                  <td className="py-3 px-4 text-center font-bold">55 CHF</td>
                  <td className="py-3 px-4 text-center font-bold">70 CHF</td>
                </tr>
                <tr className="hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4">{t.pricing.players4}</td>
                  <td className="py-3 px-4 text-center font-bold">50 CHF</td>
                  <td className="py-3 px-4 text-center font-bold">60 CHF</td>
                </tr>
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center mt-12 space-y-2"
        >
          <p className="text-slate-400 text-sm">{t.pricing.racketRental}</p>
          <p className="text-slate-300 font-semibold">{t.pricing.note}</p>
        </motion.div>

        {/* Junior Academy callout — surfaces kids' offering on the homepage */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-16"
          data-testid="pricing-junior-callout"
        >
          <Link
            to="/academy"
            className="group block relative overflow-hidden rounded-3xl border border-sky-500/30 bg-gradient-to-br from-sky-500/15 via-blue-700/10 to-transparent hover:border-sky-400/60 transition-colors p-7 md:p-10"
            data-testid="pricing-junior-link"
          >
            <div className="absolute -top-16 -right-16 w-72 h-72 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-sky-500/20 border border-sky-400/40 flex items-center justify-center">
                <Trophy className="w-7 h-7 text-sky-300" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-widest text-sky-300 mb-2">
                  {t.pricing.juniorBadge}
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
                  {t.pricing.juniorTitle}
                </h3>
                <p className="text-slate-300 text-sm md:text-base mb-3 md:max-w-2xl">
                  {t.pricing.juniorBody}
                </p>
                <p className="text-sky-200 text-sm font-semibold">
                  {t.pricing.juniorPriceFrom}
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2 text-sky-300 font-bold whitespace-nowrap group-hover:translate-x-1 transition-transform">
                {t.pricing.juniorCta}
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
            <div className="md:hidden mt-5 inline-flex items-center gap-2 text-sky-300 font-bold group-hover:translate-x-1 transition-transform">
              {t.pricing.juniorCta}
              <ArrowRight className="w-5 h-5" />
            </div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Gallery Section
